import { useId, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, LockKeyhole, Mail } from "lucide-react";
import {
  isDemoMode,
  isSupabaseConfigured,
  readRememberSession,
  setRememberSession,
  supabase,
} from "../lib/supabase";
import { BrandMark, BrandWordmark } from "../app/Brand";
import { Starfield } from "../app/Starfield";
import { ThemeToggle } from "../app/ThemeToggle";
import { appPath } from "../app/routes";
import { Button } from "../ui/Button";
import { IconButton } from "../ui/IconButton";
import { Field, TextInput } from "../ui/Field";
import { CosmosAiIcon } from "../ui/CosmosAiIcon";
import "../styles/index.css";
import "./login.css";

/**
 * Entrada do Frontend V2.
 *
 * Todos os controles têm ação real: entrar, manter conectado (decide se a
 * sessão sobrevive ao fechamento do navegador), mostrar/ocultar a senha,
 * recuperar a senha, entrar pelo Google e trocar o tema. O SSO chama o
 * provedor do Supabase; sem conta real ele fica desabilitado com o motivo, em
 * vez de simular uma entrada (§18). "Peça um convite" responde o que o produto
 * faz: cadastro público continua fechado (§9), e convites saem do administrador.
 *
 * A cena da direita é a do protótipo de entrada (`HTML/Login/login.html`):
 * céu com deriva, planeta com anel, satélites em órbita, estrelas cadentes e
 * cartões de vidro com números ILUSTRATIVOS. Ela é inteira `aria-hidden` e
 * nenhum valor dali vem do domínio — antes de entrar não há conta para medir.
 */
/**
 * Falha de rede não é senha errada: o Supabase devolve `AuthRetryableFetchError`
 * (status 0) quando o projeto está fora do ar ou a conexão caiu. Dizer
 * "verifique a senha" nesse caso manda a pessoa caçar o problema no lugar errado.
 */
function isConnectionFailure(error: { name?: string; status?: number; message?: string }) {
  return (
    error.name === "AuthRetryableFetchError" ||
    error.status === 0 ||
    /failed to fetch|networkerror|load failed/i.test(error.message ?? "")
  );
}
/** Marca do Google no botão de SSO, nas cores do próprio provedor. */
function GoogleMark() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        fill="#4285F4"
        d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.1 3.7-8.6Z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.1 0-5.8-2.1-6.7-5l-3.9 3C3.4 21.3 7.4 24 12 24Z"
      />
      <path
        fill="#FBBC05"
        d="M5.3 14.4c-.2-.7-.4-1.5-.4-2.4s.2-1.7.4-2.4l-3.9-3C.5 8.2 0 10 0 12s.5 3.8 1.4 5.4l3.9-3Z"
      />
      <path
        fill="#EA4335"
        d="M12 4.6c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1 15.2 0 12 0 7.4 0 3.4 2.7 1.4 6.6l3.9 3c.9-2.9 3.6-5 6.7-5Z"
      />
    </svg>
  );
}

function PlanetScene() {
  return (
    <section className="cx-scene" aria-hidden="true">
      <Starfield className="cx-stars" />
      <div className="cx-orbits">
        <span className="cx-ring" data-ring="outer" />
        <span className="cx-ring" />
        <span className="cx-ring" data-ring="inner" />
        <span className="cx-planet-glow" />
        <svg className="cx-planet" viewBox="-215 -215 430 430" aria-hidden="true">
          <defs>
            <radialGradient id="cx-globe" cx="34%" cy="28%" r="80%">
              <stop offset="0%" stopColor="color-mix(in srgb, var(--grad-b) 72%, white)" />
              <stop offset="52%" stopColor="var(--grad-a)" />
              <stop offset="100%" stopColor="color-mix(in srgb, var(--grad-a) 50%, black)" />
            </radialGradient>
            <radialGradient id="cx-shade" cx="30%" cy="26%" r="95%">
              <stop offset="55%" stopColor="rgba(0,0,25,0)" />
              <stop offset="100%" stopColor="rgba(0,0,25,.5)" />
            </radialGradient>
          </defs>
          {/* Metade de trás do anel; o globo passa por cima. */}
          <g transform="rotate(-14)">
            <path className="cx-ring-fill" d="M-206 0 A206 60 0 0 1 206 0 L130 0 A130 37.8 0 0 0 -130 0 Z" />
            <path className="cx-ring-edge" d="M-206 0 A206 60 0 0 1 206 0" fill="none" strokeWidth="2" />
            <path className="cx-ring-line" d="M-168 0 A168 48.9 0 0 1 168 0" fill="none" strokeWidth="2.5" />
          </g>
          <circle r="103" fill="url(#cx-globe)" />
          <circle r="103" fill="url(#cx-shade)" />
          {/* Metade da frente do anel. */}
          <g transform="rotate(-14)">
            <path className="cx-ring-fill" d="M-206 0 A206 60 0 0 0 206 0 L130 0 A130 37.8 0 0 1 -130 0 Z" />
            <path className="cx-ring-edge" d="M-206 0 A206 60 0 0 0 206 0" fill="none" strokeWidth="2" />
            <path className="cx-ring-line" d="M-168 0 A168 48.9 0 0 0 168 0" fill="none" strokeWidth="2.5" />
          </g>
        </svg>
        {/* Satélites presos às órbitas: cada um gira o quadrado da própria órbita. */}
        <div className="cx-sat" />
        <div className="cx-sat" data-sat="2" />
        <div className="cx-sat" data-sat="3" />
      </div>

      {/* Estrelas cadentes — raras, longe do planeta. */}
      <span className="cx-shoot" data-shoot="1" />
      <span className="cx-shoot" data-shoot="2" />
      <span className="cx-shoot" data-shoot="3" />

      {/*
       * Cartões de vidro com números ILUSTRATIVOS, iguais aos do protótipo. A
       * cena inteira é aria-hidden: nada aqui é dado do produto — antes de
       * entrar não existe conta para medir.
       */}
      <div className="cx-glass" data-glass="1">
        <div className="cx-glass-label">Receita da semana</div>
        <div className="cx-glass-value num">R$ 18.420</div>
        <div className="cx-glass-delta">▲ 12% vs anterior</div>
      </div>
      <div className="cx-glass" data-glass="2">
        <div className="cx-glass-label">Leads em órbita</div>
        <div className="cx-glass-value num">87</div>
        <div className="cx-glass-delta">▲ 8 novos hoje</div>
      </div>
      <div className="cx-glass" data-glass="3">
        <div className="cx-glass-msg">
          <span className="cx-glass-spark">
            <CosmosAiIcon size={16} />
          </span>
          <span>
            <b>3 propostas</b> precisam de follow-up hoje — a IA já preparou os rascunhos.
          </span>
        </div>
      </div>

      <p className="cx-scene-quote">
        Enquanto você esteve fora: <b>2 fluxos ativos</b> moveram <b>14 leads</b> pela sua
        constelação.
      </p>
    </section>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const emailId = useId();
  const passwordId = useId();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(() => readRememberSession());
  const rememberId = useId();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setNotice("");

    if (!isSupabaseConfigured || isDemoMode) {
      navigate(appPath(""));
      return;
    }

    setBusy(true);
    setRememberSession(remember);
    const { error: signInError } = await supabase!.auth.signInWithPassword({ email, password });
    setBusy(false);

    if (signInError) {
      setError(
        isConnectionFailure(signInError)
          ? "Não foi possível falar com o servidor de autenticação. Verifique sua conexão e tente de novo em instantes."
          : "Não foi possível entrar. Verifique o e-mail e a senha.",
      );
      return;
    }
    navigate(appPath(""));
  }

  async function recover() {
    setError("");
    setNotice("");
    if (!email.trim()) {
      setError("Informe seu e-mail para receber a recuperação.");
      return;
    }
    if (!supabase) return;
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${location.origin}/login`,
    });
    if (resetError) setError("Não foi possível enviar a recuperação.");
    else setNotice("E-mail de recuperação enviado.");
  }

  function invite() {
    setError("");
    // Cadastro público continua desativado (§9): quem precisa de acesso pede
    // ao administrador do workspace, e a tela diz isso em vez de abrir um form.
    setNotice("Convites são enviados pelo administrador do workspace.");
  }

  async function signInWithGoogle() {
    setError("");
    setNotice("");
    if (!supabase) return;
    setRememberSession(remember);
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${location.origin}${appPath("")}` },
    });
    if (oauthError)
      setError(
        isConnectionFailure(oauthError)
          ? "Não foi possível falar com o servidor de autenticação. Verifique sua conexão e tente de novo em instantes."
          : "Não foi possível entrar com o Google. Verifique se o provedor está habilitado na sua conta.",
      );
  }

  const realAccount = isSupabaseConfigured && !isDemoMode;

  return (
    <div className="cx-login">
      <section className="cx-login-panel">
        <div className="cx-login-head">
          <span className="cx-brand">
            <BrandMark size={32} />
            <BrandWordmark />
          </span>
          <ThemeToggle className="cx-icon-btn cx-login-theme" />
        </div>

        <div className="cx-login-body">
          <div>
            <span className="cx-login-eyebrow">Bem-vindo de volta</span>
            <h1 className="cx-login-title">Entre na sua constelação</h1>
            <p className="cx-login-sub">
              Seus leads continuaram orbitando enquanto você esteve fora.
            </p>
          </div>

          <form className="cx-login-form" onSubmit={submit} noValidate>
            <Field label="E-mail" id={emailId}>
              {({ id, describedBy, invalid }) => (
                <span className="cx-in-wrap">
                  <span className="cx-in-icon">
                    <Mail size={16} aria-hidden="true" />
                  </span>
                  <TextInput
                    id={id}
                    type="email"
                    required
                    autoComplete="username"
                    placeholder="voce@suaagencia.com.br"
                    value={email}
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </span>
              )}
            </Field>

            <Field label="Senha" id={passwordId}>
              {({ id, describedBy, invalid }) => (
                <span className="cx-in-wrap">
                  <span className="cx-in-icon">
                    <LockKeyhole size={16} aria-hidden="true" />
                  </span>
                  <TextInput
                    id={id}
                    type={showPassword ? "text" : "password"}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    data-has-action="true"
                    aria-describedby={describedBy}
                    aria-invalid={invalid}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <IconButton
                    label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                    size="compact"
                    variant="bare"
                    icon={showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    onClick={() => setShowPassword((value) => !value)}
                  />
                </span>
              )}
            </Field>

            {error && (
              <p className="cx-field-error" role="alert">
                {error}
              </p>
            )}
            {notice && (
              <p className="cx-caption" role="status">
                {notice}
              </p>
            )}

            <div className="cx-login-row">
              <label
                className="cx-remember"
                title={realAccount ? undefined : "Sem conta real não há sessão para manter."}
              >
                <input
                  id={rememberId}
                  type="checkbox"
                  checked={remember}
                  disabled={!realAccount}
                  onChange={(event) => setRemember(event.target.checked)}
                />
                Manter conectado
              </label>
              <Button
                variant="text"
                className="cx-login-link"
                onClick={recover}
                disabled={!realAccount}
                title={realAccount ? undefined : "A recuperação de senha depende de uma conta real."}
              >
                Esqueci a senha
              </Button>
            </div>

            <Button type="submit" variant="primary" loading={busy}>
              {busy ? "Alinhando os planetas…" : "Entrar"}
            </Button>
          </form>

          <div className="cx-login-divider" aria-hidden="true">
            ou continue com
          </div>

          <button
            type="button"
            className="cx-btn-sso"
            onClick={signInWithGoogle}
            disabled={!realAccount}
            title={
              realAccount
                ? undefined
                : "Entrar pelo Google exige uma conta real; a demonstração não cria sessão."
            }
          >
            <GoogleMark />
            Entrar com Google
          </button>

          <div className="cx-login-spacer" />

          <div className="cx-login-foot">
            {isDemoMode && (
              <p className="cx-login-demo">
                Modo demonstração: os dados ficam somente neste navegador e nenhuma sessão é
                criada.
              </p>
            )}
            <p>
              Ainda não tem conta?{" "}
              <Button variant="text" className="cx-login-link" onClick={invite}>
                Peça um convite
              </Button>
            </p>
          </div>
        </div>
      </section>

      <PlanetScene />
    </div>
  );
}
